export const INPUT_CAPTURE_TYPES = [
  { value: "text", label: "Text", help: "Plain text up to the configured length." },
  { value: "integer", label: "Integer", help: "Digits without grouping separators." },
  { value: "decimal", label: "Decimal", help: "Use a decimal point, not a comma." },
  { value: "email", label: "Email", help: "A standard email address." },
  { value: "phone", label: "Phone", help: "7-15 digits with an optional leading +." },
  { value: "date", label: "Date", help: "YYYY-MM-DD" },
];

const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

export const defaultInvalidResponse = inputType => {
  switch (inputType) {
    case "integer": return "Enter digits without grouping separators, for example 1000.";
    case "decimal": return "Enter a number using a decimal point, not a comma, for example 10.50.";
    case "phone": return "Enter 7-15 digits with an optional leading +.";
    case "date": return "Enter a date in YYYY-MM-DD format.";
    case "email": return "Enter a valid email address.";
    default: return "Please enter a valid response.";
  }
};

export const isInputCaptureRequestContextCompatible = (inputType, dataType) => {
  const input = String(inputType || "").trim().toLowerCase();
  const destination = String(dataType || "").trim().toLowerCase();
  if (["text", "email", "phone"].includes(input))
    return ["text", "singleselect"].includes(destination);
  if (["integer", "decimal"].includes(input)) return destination === "number";
  if (input === "date") return destination === "date";
  return false;
};

export const validateInputCaptureConfig = config => {
  const question = String(config?.question || "").trim();
  const fieldKey = String(config?.fieldKey || "").trim().toLowerCase();
  const inputType = String(config?.inputType || "").trim().toLowerCase();
  const invalidResponseMessage = String(config?.invalidResponseMessage || "").trim();
  const maximumAttempts = Number(config?.maximumAttempts);

  if (!question || question.length > 1024) return { isValid: false, message: "Question must be between 1 and 1024 characters." };
  if (!fieldKey || fieldKey.length > 128 || !FIELD_KEY_PATTERN.test(fieldKey)) return { isValid: false, message: "Field key must be a lowercase dot-separated identifier." };
  if (!INPUT_CAPTURE_TYPES.some(type => type.value === inputType)) return { isValid: false, message: "Select a supported input type." };
  if (!invalidResponseMessage || invalidResponseMessage.length > 512) return { isValid: false, message: "Invalid response guidance must be between 1 and 512 characters." };
  if (!Number.isInteger(maximumAttempts) || maximumAttempts < 1 || maximumAttempts > 5) return { isValid: false, message: "Maximum attempts must be between 1 and 5." };

  const minimum = config?.minimum;
  const maximum = config?.maximum;
  if (["integer", "decimal"].includes(inputType)) {
    if (minimum !== "" && minimum != null && !Number.isFinite(Number(minimum))) return { isValid: false, message: "Minimum must be numeric." };
    if (maximum !== "" && maximum != null && !Number.isFinite(Number(maximum))) return { isValid: false, message: "Maximum must be numeric." };
    if (minimum !== "" && minimum != null && maximum !== "" && maximum != null && Number(minimum) > Number(maximum)) return { isValid: false, message: "Minimum cannot exceed maximum." };
    if (inputType === "integer" && ((minimum !== "" && minimum != null && !Number.isInteger(Number(minimum))) || (maximum !== "" && maximum != null && !Number.isInteger(Number(maximum))))) return { isValid: false, message: "Integer bounds must be whole numbers." };
  }

  if (inputType === "text") {
    const minLength = config?.minimumLength;
    const maxLength = config?.maximumLength;
    if (minLength !== "" && minLength != null && (!Number.isInteger(Number(minLength)) || Number(minLength) < 1 || Number(minLength) > 2000)) return { isValid: false, message: "Minimum length must be between 1 and 2000." };
    if (maxLength !== "" && maxLength != null && (!Number.isInteger(Number(maxLength)) || Number(maxLength) < 1 || Number(maxLength) > 2000)) return { isValid: false, message: "Maximum length must be between 1 and 2000." };
    if (minLength !== "" && minLength != null && maxLength !== "" && maxLength != null && Number(minLength) > Number(maxLength)) return { isValid: false, message: "Minimum length cannot exceed maximum length." };
  }

  return { isValid: true, message: "", normalized: { ...config, question, fieldKey, inputType, invalidResponseMessage, maximumAttempts } };
};
